// Shared helper methods for billing components
export const billingHelpersMixin = {
  methods: {
    /**
     * Convert Firestore timestamp to localized date string
     * @param {Object} timestamp - Firestore timestamp
     * @returns {string} Formatted date string (DD.MM.YYYY)
     */
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },

    /**
     * Convert cents to formatted currency string
     * @param {number} amount - Amount in cents
     * @param {string} currency - Currency code (e.g., "EUR")
     * @returns {string} Formatted currency string
     */
    getAmountFromCents(amount, currency) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
      }).format(amount / 100);
    },

    /**
     * Get human-readable billing cycle text
     * @param {string} cycle - Billing cycle key
     * @returns {string} Localized cycle text
     */
    getBillingCycleText(cycle) {
      const cycles = {
        month: "Monat",
        year: "Jahr",
        week: "Woche",
        day: "Tag",
      };
      return cycles[cycle] || cycle;
    },

    /**
     * Get CSS class for next billing date based on proximity
     * @param {Object} nextBillingDate - Firestore timestamp
     * @returns {string} CSS class string
     */
    getNextBillingClass(nextBillingDate) {
      if (!nextBillingDate) return "";

      const now = new Date();
      const billingDate = nextBillingDate.toDate();
      const daysUntilBilling = Math.ceil(
        (billingDate - now) / (1000 * 60 * 60 * 24)
      );

      if (daysUntilBilling <= 7) {
        return "warning--text font-weight-medium";
      } else if (daysUntilBilling <= 30) {
        return "info--text";
      }
      return "";
    },

    /**
     * Get icon for change type
     * @param {string} changeType - Type of change
     * @returns {string} Material Design Icon name
     */
    getChangeTypeIcon(changeType) {
      const icons = {
        cancellation: "mdi-cancel",
        upgrade: "mdi-arrow-up-circle",
        downgrade: "mdi-arrow-down-circle",
        renewal: "mdi-refresh",
      };
      return icons[changeType] || "mdi-information";
    },

    /**
     * Get color for change type
     * @param {string} changeType - Type of change
     * @returns {string} Color name
     */
    getChangeTypeColor(changeType) {
      const colors = {
        cancellation: "error",
        upgrade: "success",
        downgrade: "warning",
        renewal: "info",
      };
      return colors[changeType] || "grey";
    },

    /**
     * Get color for license type
     * @param {string} licenseType - License type name
     * @returns {string} Color name
     */
    getLicenseTypeColor(licenseType) {
      const colors = {
        Standard: "blue",
        Pro: "green",
        Enterprise: "purple",
      };
      return colors[licenseType] || "grey";
    },

    /**
     * Get color for invoice status
     * @param {string} status - Invoice status
     * @returns {string} Color name
     */
    getInvoiceStatusColor(status) {
      const colors = {
        draft: "grey",
        open: "info",
        paid: "success",
        void: "error",
        uncollectible: "error",
      };
      return colors[status] || "grey";
    },

    /**
     * Get text class for invoice status
     * @param {string} status - Invoice status
     * @returns {string} CSS class string
     */
    getInvoiceStatusTextClass(status) {
      const classes = {
        draft: "grey--text",
        open: "info--text",
        paid: "success--text",
        void: "error--text",
        uncollectible: "error--text",
      };
      return classes[status] || "grey--text";
    },

    /**
     * Get human-readable proration reason text
     * @param {string} reason - Proration reason key
     * @returns {string} Localized reason text
     */
    getProrationReasonText(reason) {
      const reasons = {
        subscription_cycle: "Abrechnungszyklus",
        subscription_create: "Neue Lizenz",
        subscription_update: "Lizenzänderung",
      };
      return reasons[reason] || reason;
    },

    /**
     * Copy text to clipboard and show snackbar
     * @param {string} text - Text to copy
     */
    copyToClipboard(text) {
      if (navigator.clipboard && text && text !== "N/A") {
        navigator.clipboard.writeText(text);
        this.$store.commit("snackbar/setSnackbar", {
          type: "info",
          message: "In Zwischenablage kopiert",
        });
      }
    },

    /**
     * Show alert with license features
     * @param {Object} license - License object with features
     */
    showLicenseFeatures(license) {
      const features = license.features || [];
      const featureText =
        features.length > 0 ? features.join("\n• ") : "Keine Features";
      alert(`Features für ${license.title}:\n\n• ${featureText}`);
    },

    /**
     * Open invoice PDF in new tab
     * @param {string} invoicePdfUrl - URL to invoice PDF
     */
    openInvoiceInBillingPortal(invoicePdfUrl) {
      window.open(invoicePdfUrl, "_blank");
    },
  },
};
